import { Modal } from 'antd';
import styled from 'styled-components';

import colors from '~/constants/colors';

type UploadStatus = 'idle' | 'validating' | 'validated' | 'importing' | 'success' | 'error';

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    padding: 32px;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }

  .ant-modal-close-x {
    width: 32px;
    height: 32px;
    line-height: 32px;
    color: ${colors.grey[500]};
  }
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const StatusHeader = styled.div<{ status: UploadStatus }>`
  text-align: center;

  .ant-typography-title {
    margin: 0;
    color: ${({ status }) => {
        switch (status) {
            case 'success':
            case 'validated':
                return colors.successColor;
            case 'error':
                return colors.red[600];
            case 'validating':
            case 'importing':
                return colors.blue[600];
            default:
                return colors.blue[1100];
        }
    }};
    font-size: 18px;
  }
`;

export const ErrorMessage = styled.div`
  color: ${colors.errorColor};
  font-size: 14px;
  padding: 12px;
  background-color: ${colors.red[50]};
  border: 1px solid ${colors.red[200]};
  border-radius: 6px;
`;

export const SuccessMessage = styled.div`
  color: ${colors.successColor};
  font-size: 14px;
  padding: 12px;
  background-color: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;

  button {
    min-width: 100px;
  }
`;

export const ImportResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 8px;
`;

export const ResultSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: ${colors.grey[50]};
  border-radius: 8px;
  border: 1px solid ${colors.grey[200]};
`;

export const SummaryItem = styled.div<{ success?: boolean; error?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background-color: ${({ success, error }) => {
        if (success) return '#f0fdf4';
        if (error) return '#fef2f2';
        return '#ffffff';
    }};
  border: 1px solid ${({ success, error }) => {
        if (success) return '#86efac';
        if (error) return '#fecaca';
        return colors.grey[300];
    }};
  border-radius: 6px;
`;

export const SummaryLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.grey[700]};
`;

export const SummaryValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.blue[1100]};
`;

export const RowResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const RowResultsTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.grey[800]};
  margin-bottom: 4px;
`;

export const RowResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.grey[100]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.grey[400]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.grey[500]};
  }
`;

export const RowResultItem = styled.div<{ status: string }>`
  display: grid;
  grid-template-columns: 80px 100px 1fr;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background-color: ${({ status }) =>
        status.toLowerCase() === 'success' ? '#f0fdf4' : '#fef2f2'
    };
  border: 1px solid ${({ status }) =>
        status.toLowerCase() === 'success' ? '#86efac' : '#fecaca'
    };
  border-radius: 6px;
  font-size: 13px;
`;

export const RowNumber = styled.span`
  font-weight: 600;
  color: ${colors.grey[700]};
`;

export const RowStatus = styled.span<{ status: string }>`
  font-weight: 600;
  color: ${({ status }) =>
        status.toLowerCase() === 'success' ? colors.successColor : colors.errorColor
    };
`;

export const RowMessage = styled.span`
  color: ${colors.grey[600]};
  word-break: break-word;
`;
